import { AxiosError } from "axios";
import { ChangeEvent, useEffect, useState, MouseEvent } from "react";
import { Buttons, Form, FormElements } from "../../../common/AddEditFormLayout/AddEditFormLayout.styles";
import Header from "../../../common/Header/Header";
import { H2 } from "../../../common/Headings/Headings.styles";
import Button from "../../../components/Button/Button";
import Dropdown from "../../../components/Dropdown/Dropdown";
import { showToast } from "../../../components/Toast/Toast";
import { ToastTypes } from "../../../components/Toast/Toast.types";
import { OPTIONS_PLACEHOLDER } from "../../../constants/Dropdown";
import { useGetClasses } from "../../../services/class/class.service";
import { Class } from "../../../services/class/class.types";
import { Product } from '../../../services/product/product.types';
import { Option } from '../../../services/option/option.types';
import { useAddNewComboTopping } from "../../../services/comboTopping/comboTopping.service";
import { useGetProducts } from "../../../services/product/product.service";
import { genericErrorShowToastOptions } from "../../../utils/useGenericErrorMessage/useGenericErrorMessage";
import useNavigateWithToast from "../../../utils/useNavigateWithToast/useNavigateWithToast";
import { RowWrapper } from "./AddNewComboTopping.styles";
import { AddNewComboToppingFormErrors, ComboToppingFormValues } from "./AddNewComboTopping.types";
import { useGetOptions } from "../../../services/option/option.service";
import { DropdownWrapperLarge } from "../../../common/FormElementWrappers/FormElementWrappers.styles";
import { CLASS_STATUS } from '../../../constants/ApiEnums';

const AddNewComboTopping = () => {
    const navigateWithToast = useNavigateWithToast();
    const [formValues, setFormValues] = useState<ComboToppingFormValues>({
        classNo: '',
        optionNo: undefined,
        productNo: undefined
    });
    const { data: classesData } = useGetClasses(Number(CLASS_STATUS.ACTIVE));
    const { data: productsData } = useGetProducts({ classNo: +formValues.classNo, },
        formValues.classNo !== '');
    const { data: optionsData } = useGetOptions({ classNo: (+formValues.classNo).toString(), },
        formValues.classNo !== '' && formValues.productNo !== undefined);
    const { mutate, isLoading } = useAddNewComboTopping();
    const allClasses = classesData?.data || [];
    const classOptions = allClasses.map((item: Class) => {
        return { value: item?.classNo?.toString(), label: `${item?.classNo} - ${item?.description}` };
    });
    const allProducts = productsData?.data || [];
    const productOptions = allProducts.map((item: Product) => {
        return { value: item?.productNo.toString(), label: `${item?.productNo} - ${item?.description}` };
    })
    const allOptions = optionsData?.data || [];
    const options = allOptions.map((item: Option) => {
        return { value: item?.optionNo.toString(), label: `${item?.optionNo} - ${item?.description}` };
    })

    const initialFormValues = {
        classNo: undefined,
        optionNo: undefined,
        productNo: undefined
    };
    const [formErrors, setFormErrors] = useState<AddNewComboToppingFormErrors>({
        classNo: '',
        optionNo: '',
        productNo: ''
    });
    const validateFormValues = () => {
        let error = false;
        let errorMessages: AddNewComboToppingFormErrors = {
            classNo: '',
            optionNo: '',
            productNo: ''
        };

        if ([null, undefined, -1, 0].includes(Number(formValues.classNo))) {
            errorMessages.classNo = 'Class Number is required.';
            error = true;
        }

        if ([null, undefined, -1].includes(formValues.optionNo)) {
            errorMessages.optionNo = 'Option Number is required.';
            error = true;
        }

        if ([null, undefined, -1].includes(formValues.productNo)) {
            errorMessages.productNo = 'Product Number is required.';
            error = true;
        }

        setFormErrors((formErrors) => {
            return { ...formErrors, ...errorMessages };
        });
        return error;
    };

    const handleDropDown = (event: ChangeEvent<HTMLSelectElement>) => {
        setFormValues({ ...formValues, [event.target.id]: event.target.value });
        setFormErrors && setFormErrors({ ...formErrors, [event.target.id]: '' });
    };

    const [unsavedChanges, setUnsavedChanges] = useState(false);

    const checkForUnsavedChanges = () => {
        if (
            initialFormValues.classNo === formValues.classNo &&
            initialFormValues.optionNo === formValues.optionNo &&
            initialFormValues.productNo === formValues.productNo

        ) {
            setUnsavedChanges(false);
            return;
        } else {
            setUnsavedChanges(true);
            return;
        }
    };

    useEffect(() => {
        checkForUnsavedChanges();
    }, [formValues]);

    const handleSave = (event: MouseEvent) => {
        event.preventDefault();
        const isError = validateFormValues();
        if (isError) return;
        setUnsavedChanges(false);
        mutate(
            {
                classNo: formValues.classNo as number,
                optionNo: formValues.optionNo as number,
                productNo: formValues.productNo as number,
            },
            {
                onSuccess: () => {
                    navigateWithToast(`/tables/combotopping`, {
                        label: 'New Combo Topping has been added successfully',
                        id: 'comboToppingAddSuccess',
                    });
                },
                onError: (e) => {
                    if ((e as AxiosError)?.message === 'Request failed with status code 409') {
                        showToast({
                            label: 'Combo Topping already exists.',
                            id: 'addComboToppingIsError',
                            toastType: ToastTypes.Error,
                        });
                    } else {
                        showToast(genericErrorShowToastOptions);
                    }
                },
            }
        );
    };

    const filterWildCard = (dropdown: {value: string, label: string}) => {
        return dropdown.value !== '999'
    }

    return (
        <>
            <Header titleTestId="AddNewComboToppingTitle" breadcrumb={{ to: '/tables/combotopping', label: 'Combo Toppings' }} title='Add New Combo Topping' />
            <Form>
                <FormElements>
                    <H2>Combo Topping Table</H2>
                    <RowWrapper>
                        <DropdownWrapperLarge>
                            <Dropdown
                                dataTestId="classNoDropdown"
                                label='Class Number'
                                id='classNo'
                                options={classOptions.filter(filterWildCard)}
                                value={formValues?.classNo?.toString() || OPTIONS_PLACEHOLDER}
                                onChange={handleDropDown}
                                errorText={formErrors?.classNo}
                            />
                        </DropdownWrapperLarge>
                        <DropdownWrapperLarge>
                            <Dropdown
                                dataTestId="productNoDropdown"
                                label='Product Number'
                                id='productNo'
                                options={productOptions.filter(filterWildCard)}
                                value={formValues?.productNo?.toString() || OPTIONS_PLACEHOLDER}
                                onChange={handleDropDown}
                                errorText={formErrors?.productNo}
                                disabled={formValues.classNo === ''}
                            />
                        </DropdownWrapperLarge>
                        <DropdownWrapperLarge>
                            <Dropdown
                                dataTestId="optionNoDropdown"
                                label='Option Number'
                                id='optionNo'
                                options={options.filter(filterWildCard)}
                                value={formValues?.optionNo?.toString() || OPTIONS_PLACEHOLDER}
                                onChange={handleDropDown}
                                errorText={formErrors?.optionNo}
                                disabled={formValues.classNo === '' && formValues.productNo === undefined}
                            />
                        </DropdownWrapperLarge>
                    </RowWrapper>
                </FormElements>
                <Buttons>
                    <Button label='Save' testId="saveNewComboTopping" onClick={handleSave} disabled={!unsavedChanges} isLoading={isLoading} />
                </Buttons>
            </Form>
        </>
    )
}

export default AddNewComboTopping;